import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CloseIcon } from '@chakra-ui/icons';
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  IconButton,
  useBreakpointValue,
} from '@chakra-ui/react';
import { Page, PageType, RelationshipType } from '@common/entities';
import { RootState } from 'state/store';

import ApplicationNavigation from 'components/navigation/leasing-journey/main/ApplicationNavigation';
import { hasDueNowPaymentRequired } from 'utils/charge-utils';
import { filterApplicationPages } from 'utils/filterApplicationPages';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onFilteredPages: (pages: Page[]) => void;
  isNavigationLoading?: boolean;
  onNavigationStart?: () => void;
  onNavigationEnd?: () => void;
}

export default function MobileNavigation({
  isOpen,
  onClose,
  onFilteredPages,
  isNavigationLoading = false,
  onNavigationStart = () => undefined,
  onNavigationEnd = () => undefined,
}: Props) {
  const [filteredPages, setFilteredPages] = useState<Page[]>([]);

  const { isLeadProfile } = useSelector(
    (state: RootState) => state.auth ?? { isLeadProfile: false },
  );
  const applicationForm = useSelector((state: RootState) => state.application.form);
  const { charges, scheduledChargesLoaded } = useSelector((state: RootState) => state.shoppingCart);
  const { currencyCode } = useSelector((state: RootState) => state.locale);
  const { currentTabIndex, activeStepByTab } = useSelector((state: RootState) => state.navigation);
  const activeStep = activeStepByTab[currentTabIndex];

  const drawerPlacement: 'top' | 'left' = useBreakpointValue({ base: 'top', md: 'left' }) || 'top';
  const drawerSize: 'sm' | 'xs' = useBreakpointValue({ base: 'sm', md: 'xs' }) || 'sm';

  useEffect(() => {
    // Filter pages to remove hideFrom, hideFromGuarantor, and the thank you page.
    const hasDueNowCharges = hasDueNowPaymentRequired(charges, currencyCode);
    const isActivePaymentStep = applicationForm.pages.some(
      (page) => page.id === activeStep && page.type === PageType.Payment,
    );
    const filteredApplicationPages: Page[] = filterApplicationPages(
      applicationForm,
      RelationshipType.Primary,
      scheduledChargesLoaded ? hasDueNowCharges || isActivePaymentStep : undefined,
      isLeadProfile,
    );

    setFilteredPages(filteredApplicationPages);
    onFilteredPages(filteredApplicationPages);
  }, [
    activeStep,
    applicationForm,
    charges,
    currencyCode,
    scheduledChargesLoaded,
    onFilteredPages,
    isLeadProfile,
  ]);

  const handleBackClick = () => {
    onClose();
  };

  return (
    <Drawer onClose={onClose} placement={drawerPlacement} isOpen={isOpen} size={drawerSize}>
      <DrawerOverlay />
      <DrawerContent
        bg="white.base"
        borderBottomLeftRadius={{ base: '2xl', md: 'none' }}
        borderTopRightRadius={{ base: 'none', md: '2xl' }}
        borderBottomRightRadius={{ base: '2xl', md: '2xl' }}
        maxH={{ base: 'calc(100% - 80px)', md: '100%' }}
      >
        <DrawerHeader bg="rgb(255 255 255 / 10%)" fontWeight={800} fontSize={18}>
          <IconButton aria-label="Menu" bg="none" icon={<CloseIcon />} onClick={handleBackClick} />
        </DrawerHeader>
        <DrawerBody px="30px" pt="0" pb={8}>
          <ApplicationNavigation
            filteredPages={filteredPages}
            onClose={onClose}
            isNavigationLoading={isNavigationLoading}
            onNavigationStart={onNavigationStart}
            onNavigationEnd={onNavigationEnd}
          />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
